import type { Theme } from '@emotion/react';
import { css } from '@emotion/react';
import { device } from './devices';

interface ThemeProps {
  theme: Theme;
}

export function responseFont(minSize: number, size: number, limit?: number) {
  return (props: ThemeProps) => {
    const { theme } = props;

    limit = limit ?? theme.pageLimit({ theme });
    const baseline = theme.breakpoints.sm.width;
    const fontMultiplier = (size - minSize) / ((limit ?? 0) - baseline);
    const fontBaseline = minSize - fontMultiplier * baseline;

    return css`
      font-size: ${minSize}px;

      @media (min-width: ${theme.breakpoints.sm.width}px) {
        font-size: calc(${fontMultiplier} * 100vw + (${fontBaseline}px));
      }

      @media (min-width: ${limit}px) {
        font-size: ${size}px;
      }
    `;
  };
}

export const dynamicInput = () => css`
  position: absolute;
  left: -9999px;
`;

export function mobileGutterMargin({ theme }: ThemeProps) {
  const minGutter = 16;
  const baseline = 320;

  const gutterMultiplier =
    (theme.breakpoints.sm.gutter - minGutter) /
    (theme.breakpoints.sm.width - baseline);
  const gutterBaseline = minGutter - gutterMultiplier * baseline;
  return css`
    @media (max-width: ${theme.breakpoints.sm.width}px) {
      margin-left: calc(
        -1 * (${gutterMultiplier} * 100vw + (${gutterBaseline}px))
      );
      margin-right: calc(
        -1 * (${gutterMultiplier} * 100vw + (${gutterBaseline}px))
      );
    }
  `;
}

export function mobileGutterPadding({ theme }: ThemeProps) {
  const minGutter = 16;
  const baseline = 320;

  const gutterMultiplier =
    (theme.breakpoints.sm.gutter - minGutter) /
    (theme.breakpoints.sm.width - baseline);
  const gutterBaseline = minGutter - gutterMultiplier * baseline;
  return css`
    @media (max-width: ${theme.breakpoints.sm.width}px) {
      padding-left: calc(${gutterMultiplier} * 100vw + (${gutterBaseline}px));
      padding-right: calc(${gutterMultiplier} * 100vw + (${gutterBaseline}px));
    }
  `;
}

export function container({ theme }: ThemeProps) {
  return css`
    flex-grow: 1;
    margin: 0 auto;
    padding-left: ${theme.breakpoints.sm.gutter}px;
    padding-right: ${theme.breakpoints.sm.gutter}px;
    max-width: ${theme.pageWidth + theme.breakpoints.sm.gutter * 2}px;

    ${mobileGutterPadding({ theme })}

    @media (min-width: ${theme.breakpoints.md.width}px) {
      padding-left: ${theme.breakpoints.md.gutter}px;
      padding-right: ${theme.breakpoints.md.gutter}px;
      max-width: ${theme.pageWidth + theme.breakpoints.md.gutter * 2}px;
    }

    @media (min-width: ${theme.breakpoints.lg.width}px) {
      padding-left: ${theme.breakpoints.lg.gutter}px;
      padding-right: ${theme.breakpoints.lg.gutter}px;
      max-width: ${theme.pageWidth + theme.breakpoints.lg.gutter * 2}px;
    }
  `;
}

export function fit() {
  return css`
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
  `;
}

export function sronly() {
  return css`
    position: absolute;
    left: -10000px;
    top: auto;
    width: 1px;
    height: 1px;
    overflow: hidden;
  `;
}

export function smallTable(background?: string) {
  return (props: { theme: Theme }) => {
    const { theme } = props;
    const wrapperBgColor =
      background === 'Gray'
        ? theme.colors.backgroundAlt
        : theme.colors.background;
    const underLineBorder = `3px solid ${theme.colors.foreground}`;

    return css`
      background-color: ${wrapperBgColor};
      table {
        width: 100%;
        text-align: right;
        th {
          padding: 9px 0;
          font-family: ${theme.font.familySerifCond};
          font-size: 18px;
          line-height: ${24 / 18};
          font-weight: 500;
          text-align: left;
          border-bottom: ${underLineBorder};
        }
        td {
          padding: 6px 0 7px;
          border-bottom: 1px solid rgba(30, 61, 90, 0.1);
          font-size: 14px;
          line-height: ${18 / 14};
          &:first-child {
            text-align: left;
          }
        }
        tbody {
          tr:first-child td {
            padding-top: 12px;
          }
          tr:last-child td {
            padding-bottom: 14px;
            border-bottom: 1px solid transparent;
            border-color: ${theme.colors.primary};
          }
        }
        tbody:last-child tr:last-child td {
          border-bottom-color: transparent;
        }
        strong {
          font-weight: 500;
        }
        b {
          font-weight: 500;
        }
      }
    `;
  };
}

export function zebraTable(background?: string, colorTheme?: string) {
  return (props: { theme: Theme }) => {
    const { theme } = props;
    const wrapperBgColor =
      background === 'Gray'
        ? theme.colors.backgroundAlt
        : theme.colors.background;
    const themeColor = theme.colors[colorTheme ?? 'primary'];
    const shadeColor = theme.shades[`${colorTheme}1`];
    const tdBgColor =
      background === 'Gray' && colorTheme === 'primary' ? '#fff' : shadeColor;

    return css`
      overflow-x: auto;
      background-color: ${wrapperBgColor};

      table {
        width: 100%;
        text-align: right;
        position: relative;
        border-collapse: separate;

        th {
          padding: 12px 10px;
          font-family: ${theme.font.familySerifCond};
          border-bottom: 2px solid ${themeColor};

          position: sticky;
          top: 0;
          z-index: 2;
          background-color: #fff;
          white-space: nowrap;

          &:last-child {
            padding-right: 0;
          }

          &:first-child {
            text-align: left;
            left: 0;
            z-index: 3;
          }
        }

        td {
          padding: 17px 10px;
          border-bottom: 1px solid ${themeColor};
          background-color: ${wrapperBgColor};

          &:first-child {
            position: sticky;
            left: 0;
            text-align: left;
          }

          &:last-of-type {
            text-align: right;
          }
        }

        tr:nth-child(2n) {
          td {
            background-color: ${tdBgColor};
          }
        }

        tfoot {
          tr {
            td {
              padding: 22px 10px;
              font-weight: 500;
              border: none;
            }
            td:first-child {
              text-align: left;
            }
          }
        }
      }
    `;
  };
}

export function standardTable(
  background?: string,
  noHeadingUnderline?: boolean,
) {
  return (props: { theme: Theme }) => {
    const { theme } = props;
    const wrapperBgColor =
      background === 'Gray'
        ? theme.colors.backgroundAlt
        : theme.colors.background;
    const underLineSpace = noHeadingUnderline ? '30px' : 'inherit';
    const underLineBorder = noHeadingUnderline
      ? '2px solid transparent'
      : `2px solid ${theme.colors.foreground}`;
    const underLineDisplay = noHeadingUnderline ? 'inline-block' : 'none';

    return css`
      background-color: ${wrapperBgColor};

      table {
        width: 100%;
        text-align: right;

        th {
          padding: 12px 0;
          padding-bottom: ${underLineSpace};
          font-family: ${theme.font.familySerifCond};

          border-bottom: ${underLineBorder};

          &:first-child {
            position: relative;
            text-align: left;
            font-size: 30px;
            font-weight: 500;

            &:after {
              content: '';
              display: ${underLineDisplay};
              position: absolute;
              bottom: 10px;
              left: 0;
              display: inline-block;
              height: 1px;
              width: 27px;
              background-color: currentColor;
            }
          }
        }

        td {
          padding: 7px 0;

          &:first-child {
            text-align: left;
          }
        }

        tbody {
          tr:first-child td {
            padding-top: 14px;
          }

          tr:last-child td {
            padding-bottom: 14px;
            border-bottom: 1px solid transparent;
            border-color: ${theme.colors.primary};
          }
        }

        tbody:last-child tr:last-child td {
          border-bottom-color: transparent;
        }

        strong {
          font-weight: 500;
        }
      }
    `;
  };
}

export function stickyFirstColumn() {
  return css`
    overflow-x: auto;
    overflow-y: hidden;

    table {
      width: 100%;
      position: relative;
      border-collapse: separate;
      tr {
        left: 0;
      }
      th {
        position: sticky;
        z-index: 2;
        background-color: #fff;

        &:first-child {
          left: 0;
          z-index: 3;
          max-width: 180px;
          box-shadow: 5px 10px 8px #e8ebee;
          @media ${device.desktop} {
            box-shadow: none;
          }
        }
        /* Mix svo box-shadow fari ekki yfir th borderinn */
        &:first-child:after {
          content: '';
          background: rgb(32, 68, 106);
          height: 3px;
          display: block;
          width: 120%;
          position: Absolute;
          bottom: -3px;
          left: 0;
          z-index: 4;
        }
      }

      td {
        &:first-child {
          position: sticky !important;
          left: 0;
          z-index: 5 !important;
          background-color: #fff;
          box-shadow: 5px 10px 8px #e8ebee;

          @media ${device.desktop} {
            box-shadow: none;
          }
        }
      }
    }
  `;
}
